import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ShiftCard = ({ shift, isExpanded, onToggle }) => {
  const [showActions, setShowActions] = useState(false);

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'bg-success/10 text-success border-success/20';
      case 'pending':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'cancelled':
        return 'bg-error/10 text-error border-error/20';
      default:
        return 'bg-muted/10 text-muted-foreground border-border';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'confirmed':
        return 'Bestätigt';
      case 'pending':
        return 'Ausstehend';
      case 'cancelled':
        return 'Storniert';
      default:
        return 'Unbekannt';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('de-DE', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    return new Date(`2000-01-01T${timeString}`)?.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-card hover:shadow-modal transition-shadow">
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Briefcase" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">{shift?.title}</h3>
              <p className="text-sm text-muted-foreground">{shift?.code}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(shift?.status)}`}>
              {getStatusLabel(shift?.status)}
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggle}
              className="w-8 h-8"
            >
              <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={16} />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-3">
          <div className="flex items-center space-x-2">
            <Icon name="Calendar" size={16} className="text-muted-foreground" />
            <span className="text-sm text-foreground">{formatDate(shift?.date)}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Clock" size={16} className="text-muted-foreground" />
            <span className="text-sm text-foreground">
              {formatTime(shift?.startTime)} - {formatTime(shift?.endTime)}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="MapPin" size={16} className="text-muted-foreground" />
            <span className="text-sm text-foreground">{shift?.location}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="DollarSign" size={16} className="text-muted-foreground" />
            <span className="text-sm text-foreground">{shift?.payRate}€/Std.</span>
          </div>
        </div>

        {isExpanded && (
          <div className="border-t border-border pt-4 mt-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium text-foreground mb-2">Supervisor</h4>
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center">
                    <Icon name="User" size={14} color="white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{shift?.supervisor?.name}</p>
                    <p className="text-xs text-muted-foreground">{shift?.supervisor?.phone}</p>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="font-medium text-foreground mb-2">Pausenzeiten</h4>
                <div className="space-y-1">
                  {shift?.breaks?.map((breakTime, index) => (
                    <p key={index} className="text-sm text-foreground">
                      {formatTime(breakTime?.start)} - {formatTime(breakTime?.end)} ({breakTime?.duration} Min.)
                    </p>
                  ))}
                </div>
              </div>
            </div>

            {shift?.requirements && shift?.requirements?.length > 0 && (
              <div>
                <h4 className="font-medium text-foreground mb-2">Anforderungen</h4>
                <div className="flex flex-wrap gap-2">
                  {shift?.requirements?.map((req, index) => (
                    <span key={index} className="px-2 py-1 bg-accent/10 text-accent text-xs rounded-full">
                      {req}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {shift?.notes && (
              <div>
                <h4 className="font-medium text-foreground mb-2">Notizen</h4>
                <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
                  {shift?.notes}
                </p>
              </div>
            )}

            <div className="flex items-center justify-between pt-4 border-t border-border">
              <div className="flex items-center space-x-2">
                <Icon name="Users" size={16} className="text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  Team: {shift?.teamSize} Personen
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="MessageSquare"
                  iconPosition="left"
                  iconSize={14}
                  onClick={() => console.log('Contact supervisor')}
                >
                  Kontakt
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="MoreHorizontal"
                  iconSize={14}
                  onClick={() => setShowActions(!showActions)}
                />
              </div>
            </div>

            {showActions && (
              <div className="bg-muted/30 rounded-lg p-3 space-y-2">
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="RefreshCw"
                  iconPosition="left"
                  iconSize={14}
                  className="w-full justify-start"
                  onClick={() => console.log('Request swap')}
                >
                  Schichttausch beantragen
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="X"
                  iconPosition="left"
                  iconSize={14}
                  className="w-full justify-start text-error hover:text-error"
                  onClick={() => console.log('Cancel shift')}
                >
                  Schicht stornieren
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShiftCard;